import classNames from 'classnames'
import capitalize from 'lodash/capitalize'
import find from 'lodash/find'
import get from 'lodash/get'
import moment from 'moment'
import React from 'react'
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { i18n } from '../index'
import css from './customerTargetGraph.css'
import type { DailySales } from './customerTargetTypes'
import { getProductGroupLabel, getProductGroupName } from './helpers'
type Props = {
  week: string
  productGroup: string
  dailySales: Array<DailySales>
  salesDetails: Array<{
    notes: string
    date: string
  }>
}
type State = {
  isActive: boolean
  date: string
  sales: number
  target: number
}
type WeekType = 'WeekAgo' | 'YearAgo' | 'TwoWeeksAgo' | 'ThreeWeeksAgo'
const CustomerTargetWeek: { [key: string]: WeekType } = {
  WeekAgo: 'WeekAgo',
  YearAgo: 'YearAgo',
  TwoWeeksAgo: 'TwoWeeksAgo',
  ThreeWeeksAgo: 'ThreeWeeksAgo',
}

type GraphData = {
  date: string
  salesThreeWeeksAgo: number
  salesTwoWeeksAgo: number
  salesWeekAgo: number
  salesYearAgo: number
  targetThreeWeeksAgo: number
  targetTwoWeeksAgo: number
  targetWeekAgo: number
  targetYearAgo: number
}

const getLastYearWeekDay = (week: string | moment.Moment, date: string | moment.Moment): moment.Moment => {
  return moment(week).subtract(52, 'weeks').startOf('week').weekday(moment(date).weekday())
}

const getGraphData = (dailySales: Array<DailySales>, productGroup: string, week: string) => {
  const data: GraphData[] = []

  const getDailySalesItems = (date): any => {
    // skip the week before our intended planning week
    const threeWeeksAgo = moment(date).subtract(4, 'weeks')
    const twoWeeksAgo = moment(date).subtract(3, 'weeks')
    const weekAgo = moment(date).subtract(2, 'weeks')
    const yearAgo = getLastYearWeekDay(week, date)
    const format = 'DD.MM.YYYY'
    const productGroupName = getProductGroupName(productGroup)

    const isSame = (date, date2) => {
      return date.format(format) === date2.format(format)
    }

    return {
      threeWeeksAgo: find(
        dailySales,
        (item) => item.productGroup === productGroupName && isSame(moment(item.date), threeWeeksAgo),
      ),
      twoWeeksAgo: find(
        dailySales,
        (item) => item.productGroup === productGroupName && isSame(moment(item.date), twoWeeksAgo),
      ),
      weekAgo: find(dailySales, (item) => item.productGroup === productGroupName && isSame(moment(item.date), weekAgo)),
      yearAgo: find(dailySales, (item) => item.productGroup === productGroupName && isSame(moment(item.date), yearAgo)),
    }
  }

  for (let i = 0; i < 7; ++i) {
    const date = moment(week).startOf('week').add(i, 'days')
    const items = getDailySalesItems(date)
    data.push({
      date: date.format('YYYY-MM-DD'),
      salesThreeWeeksAgo: get(items, 'threeWeeksAgo.salesVolume', 0) || 0,
      salesTwoWeeksAgo: get(items, 'twoWeeksAgo.salesVolume', 0) || 0,
      salesWeekAgo: get(items, 'weekAgo.salesVolume', 0) || 0,
      salesYearAgo: get(items, 'yearAgo.salesVolume', 0) || 0,
      targetThreeWeeksAgo: get(items, 'threeWeeksAgo.target', 0) || 0,
      targetTwoWeeksAgo: get(items, 'twoWeeksAgo.target', 0) || 0,
      targetWeekAgo: get(items, 'weekAgo.target', 0) || 0,
      targetYearAgo: get(items, 'yearAgo.target', 0) || 0,
    })
  }

  return data
}

const TargetLine = ({ x, y, width }: any) => {
  return (
    <line
      x1={x - width * 0.1}
      x2={x + width + width * 0.2}
      y1={y}
      y2={y}
      stroke='black'
      strokeWidth='3'
      strokeDasharray={`${(width + width * 0.2) / 5}`}
    />
  )
}

const YAxisTick = (props: any) => {
  return (
    <text x={props.x} y={props.y} textAnchor='end' alignmentBaseline='baseline' className={css.yAxisTick}>
      {props.payload.value}
    </text>
  )
}

const XAxisTick = (props: any) => {
  const date = moment(props.payload.value)
  const isSunday = Number(date.format('E')) === 7
  return (
    <text
      x={props.x}
      y={props.y + props.height / 2}
      textAnchor='middle'
      className={classNames(css.xAxisTick, {
        [css.isSunday]: isSunday,
      })}
    >
      {capitalize(date.format('dddd D.M.'))}
    </text>
  )
}

const CustomTooltip = (props: any) => {
  const { date, sales, target, details } = props
  const notes = details && details.notes
  return (
    <div className={css.tooltip}>
      <div>{date}</div>
      <div className={css.salesInd}>
        {i18n.t('customerTarget.salesLabel')}: {sales}
      </div>
      <div>
        {i18n.t('customerTarget.targetLabel')}: {target}
      </div>
      {notes && (
        <div>
          {i18n.t('customerTarget.notesLabel')}:
          <br />
          {notes}
        </div>
      )}
    </div>
  )
}

class CustomerTargetGraph extends React.Component<Props, State> {
  state = {
    isActive: false,
    date: '',
    sales: 0,
    target: 0,
  }
  static getDataMinDomain = (dataMin: number) => {
    if (dataMin % 10 === 0) {
      return Math.max(dataMin - 10, 0)
    }

    return Math.max(dataMin - (dataMin % 10), 0)
  }
  static getDataMaxDomain = (dataMax: number) => {
    if (dataMax % 10 === 0) {
      return dataMax + 10
    }

    return dataMax + (10 - (dataMax % 10))
  }
  displayTooltip = (data: any, weekType: WeekType) => {
    let date = ''
    const sales = data[`sales${weekType}`],
      target = data[`target${weekType}`]
    const weekBeforePlanning = moment(data.date).subtract(1, 'weeks')

    switch (weekType) {
      case CustomerTargetWeek.WeekAgo:
        date = weekBeforePlanning.subtract(1, 'weeks').format('YYYY-MM-DD')
        break

      case CustomerTargetWeek.TwoWeeksAgo:
        date = weekBeforePlanning.subtract(2, 'weeks').format('YYYY-MM-DD')
        break

      case CustomerTargetWeek.ThreeWeeksAgo:
        date = weekBeforePlanning.subtract(3, 'weeks').format('YYYY-MM-DD')
        break

      case CustomerTargetWeek.YearAgo:
        date = getLastYearWeekDay(this.props.week, data.date).format('YYYY-MM-DD')
        break

      default:
        break
    }

    this.setState({
      isActive: true,
      date,
      sales,
      target,
    })
  }
  hideTooltip = () =>
    this.setState({
      isActive: false,
    })

  render() {
    const { productGroup, salesDetails, dailySales, week } = this.props
    const { isActive, date, sales, target } = this.state
    const details = salesDetails.find((details) => details.date === date)
    return (
      <div className={css.component}>
        <div className={css.legend}>
          <p className={css.title}>{getProductGroupLabel(productGroup)}</p>
          <p className={css.targetLabel}>
            <span className={css.targetIcon} />
            {i18n.t('customerTarget.targetLabel')}
          </p>
          <p className={css.lastYearLabel}>
            <span className={css.lastYearIcon} />
            <span>{i18n.t('customerTarget.lastYearLabel')}</span>
          </p>
        </div>
        <div className={css.graph}>
          <div className={css.graphWrapper}>
            <ResponsiveContainer>
              <ComposedChart
                data={getGraphData(dailySales, productGroup, week)}
                margin={{
                  top: 0,
                  right: 0,
                  bottom: 8,
                  left: -30,
                }}
                barCategoryGap='15%'
                barGap={15}
              >
                <CartesianGrid stroke='#ccc' vertical={false} />
                <XAxis xAxisId={0} dataKey='date' tickLine={false} tick={<XAxisTick />} />
                <XAxis xAxisId={1} dataKey='date' hide />
                <YAxis
                  width={80}
                  tickLine={false}
                  tick={<YAxisTick />}
                  scale='linear'
                  domain={[CustomerTargetGraph.getDataMinDomain, CustomerTargetGraph.getDataMaxDomain]}
                />

                {isActive && (
                  <Tooltip
                    cursor={false}
                    content={<CustomTooltip date={date} sales={sales} target={target} details={details} />}
                  />
                )}

                <Bar
                  dataKey={`sales${CustomerTargetWeek.YearAgo}`}
                  xAxisId={0}
                  fill='#F7AC01'
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.YearAgo)}
                />
                <Bar
                  dataKey={`sales${CustomerTargetWeek.ThreeWeeksAgo}`}
                  xAxisId={0}
                  fill='#78A22F'
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.ThreeWeeksAgo)}
                />
                <Bar
                  dataKey={`sales${CustomerTargetWeek.TwoWeeksAgo}`}
                  xAxisId={0}
                  fill='#78A22F'
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.TwoWeeksAgo)}
                />
                <Bar
                  dataKey={`sales${CustomerTargetWeek.WeekAgo}`}
                  xAxisId={0}
                  fill='#78A22F'
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.WeekAgo)}
                />
                <Bar
                  dataKey={`target${CustomerTargetWeek.YearAgo}`}
                  xAxisId={1}
                  shape={<TargetLine />}
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.YearAgo)}
                />
                <Bar
                  dataKey={`target${CustomerTargetWeek.ThreeWeeksAgo}`}
                  xAxisId={1}
                  shape={<TargetLine />}
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.ThreeWeeksAgo)}
                />
                <Bar
                  dataKey={`target${CustomerTargetWeek.TwoWeeksAgo}`}
                  xAxisId={1}
                  shape={<TargetLine />}
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.TwoWeeksAgo)}
                />
                <Bar
                  dataKey={`target${CustomerTargetWeek.WeekAgo}`}
                  xAxisId={1}
                  shape={<TargetLine />}
                  onMouseOver={(data) => this.displayTooltip(data, CustomerTargetWeek.WeekAgo)}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  }
}

export default CustomerTargetGraph
